import type { RendererState } from './renderer-state';
import { CLIMATE_RENDER_STRIDE, ClimateField as C } from '../worker/climate-protocol';
import { VEGETATION_VALUE_COUNT } from '../simulation/vegetation-field';
import { CELESTIAL_BODY_SPECS } from '../simulation/celestial-system';
import { collisionRadiusForKind } from '../simulation/collisions';
import { EntityKind } from '../simulation/primary-store';
import {
  createBuffer,
  createProgram,
  createVertexArray,
  requireUniform,
} from './gl-utils';
import {
  PRIMARY_FRAGMENT_SHADER,
  PRIMARY_VERTEX_SHADER,
} from './shaders/primary';

export class PrimaryPointRenderer {
  private readonly program: WebGLProgram;
  private readonly vertexArray: WebGLVertexArrayObject;
  private readonly buffers: readonly WebGLBuffer[];
  private readonly attributeLocations: readonly number[];
  private readonly cornerBuffer: WebGLBuffer;
  private readonly entityIndexBuffer: WebGLBuffer;
  private readonly entityIndexLocation: number;
  private readonly interpolationLocation: WebGLUniformLocation;
  private readonly pointScaleLocation: WebGLUniformLocation;
  private readonly worldPointScaleLocation: WebGLUniformLocation;
  private readonly projectionScaleLocation: WebGLUniformLocation;
  private readonly viewportSizeLocation: WebGLUniformLocation;
  private readonly cameraCenterLocation: WebGLUniformLocation;
  private readonly cameraZoomLocation: WebGLUniformLocation;
  private readonly starPositionLocation: WebGLUniformLocation;
  private readonly simulationTimeLocation: WebGLUniformLocation;
  private readonly vegetationLocation: WebGLUniformLocation;
  private readonly previousPlanetPositionsLocation: WebGLUniformLocation;
  private readonly currentPlanetPositionsLocation: WebGLUniformLocation;
  private readonly planetRadiiLocation: WebGLUniformLocation;
  private readonly collisionRadiiLocation: WebGLUniformLocation;
  private readonly bodyMaterialLocation: WebGLUniformLocation;
  private readonly bodyClimateLocation: WebGLUniformLocation;
  private readonly bodyIceColourLocation: WebGLUniformLocation;
  private readonly bodyCloudColourLocation: WebGLUniformLocation;
  private readonly bodyIceColour = new Float32Array(CELESTIAL_BODY_SPECS.length * 4);
  private readonly bodyCloudColour = new Float32Array(CELESTIAL_BODY_SPECS.length * 4);
  private readonly bodyClimate = new Float32Array(CELESTIAL_BODY_SPECS.length * 4);
  private readonly bodyLowColorLocation: WebGLUniformLocation;
  private readonly bodyHighColorLocation: WebGLUniformLocation;
  private readonly bodyLiquidColorLocation: WebGLUniformLocation;
  private readonly bodyAtmosphereColorLocation: WebGLUniformLocation;
  private readonly vegetation = new Float32Array(VEGETATION_VALUE_COUNT);
  private readonly previousPlanetPositions = new Float32Array(
    CELESTIAL_BODY_SPECS.length * 2,
  );
  private readonly currentPlanetPositions = new Float32Array(
    CELESTIAL_BODY_SPECS.length * 2,
  );
  private readonly planetRadii = Float32Array.from(
    CELESTIAL_BODY_SPECS.map((planet) => planet.bodyRadius),
  );
  private readonly collisionRadii = Float32Array.from(
    { length: EntityKind.Asteroid + 1 },
    (_value, kind) => collisionRadiusForKind(kind as EntityKind),
  );
  private readonly bodyMaterial = Float32Array.from(
    CELESTIAL_BODY_SPECS.flatMap(({ appearance }) => [
      appearance.surfaceClass,
      appearance.atmosphereStrength,
      appearance.liquidLevel,
      appearance.greenLifeStrength,
    ]),
  );
  private readonly bodyLowColor = appearanceColorArray('lowColor');
  private readonly bodyHighColor = appearanceColorArray('highColor');
  private readonly bodyLiquidColor = appearanceColorArray('liquidColor');
  private readonly bodyAtmosphereColor = appearanceColorArray('atmosphereColor');
  private count = 0;
  private renderState: RendererState | undefined;

  public constructor(
    private readonly gl: WebGL2RenderingContext,
    capacity: number,
  ) {
    const rollback: Array<() => void> = [];
    const ownBuffer = () => {
      const buffer = createBuffer(gl);
      rollback.push(() => gl.deleteBuffer(buffer));
      return buffer;
    };
    try {
    this.program = createProgram(
      gl,
      PRIMARY_VERTEX_SHADER,
      PRIMARY_FRAGMENT_SHADER,
    );
    rollback.push(() => gl.deleteProgram(this.program));
    this.vertexArray = createVertexArray(gl);
    rollback.push(() => gl.deleteVertexArray(this.vertexArray));
    const names = [
      'a_previousX',
      'a_previousY',
      'a_currentX',
      'a_currentY',
      'a_mass',
      'a_kind',
    ] as const;
    const buffers: WebGLBuffer[] = [];
    const attributeLocations: number[] = [];
    gl.bindVertexArray(this.vertexArray);
    this.cornerBuffer = ownBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.cornerBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW,
    );
    const cornerLocation = gl.getAttribLocation(this.program, 'a_corner');
    if (cornerLocation < 0) {
      throw new Error('Primary quad attribute a_corner was not found.');
    }
    gl.enableVertexAttribArray(cornerLocation);
    gl.vertexAttribPointer(cornerLocation, 2, gl.FLOAT, false, 0, 0);
    for (const name of names) {
      const buffer = ownBuffer();
      buffers.push(buffer);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        capacity * Float32Array.BYTES_PER_ELEMENT,
        gl.DYNAMIC_DRAW,
      );
      const location = gl.getAttribLocation(this.program, name);
      if (location < 0) {
        throw new Error(`Primary point attribute ${name} was not found.`);
      }
      attributeLocations.push(location);
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, 1, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(location, 1);
    }
    this.entityIndexBuffer = ownBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.entityIndexBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      Float32Array.from({ length: capacity }, (_value, index) => index),
      gl.STATIC_DRAW,
    );
    this.entityIndexLocation = gl.getAttribLocation(this.program, 'a_entityIndex');
    if (this.entityIndexLocation < 0) {
      throw new Error('Primary instance attribute a_entityIndex was not found.');
    }
    gl.enableVertexAttribArray(this.entityIndexLocation);
    gl.vertexAttribPointer(this.entityIndexLocation, 1, gl.FLOAT, false, 0, 0);
    gl.vertexAttribDivisor(this.entityIndexLocation, 1);
    gl.bindVertexArray(null);
    this.buffers = buffers;
    this.attributeLocations = attributeLocations;
    this.interpolationLocation = requireUniform(
      gl,
      this.program,
      'u_interpolation',
    );
    this.pointScaleLocation = requireUniform(gl, this.program, 'u_pointScale');
    this.worldPointScaleLocation = requireUniform(
      gl,
      this.program,
      'u_worldPointScale',
    );
    this.projectionScaleLocation = requireUniform(
      gl,
      this.program,
      'u_projectionScale',
    );
    this.viewportSizeLocation = requireUniform(gl, this.program, 'u_viewportSize');
    this.cameraCenterLocation = requireUniform(
      gl,
      this.program,
      'u_cameraCenter',
    );
    this.cameraZoomLocation = requireUniform(gl, this.program, 'u_cameraZoom');
    this.starPositionLocation = requireUniform(gl, this.program, 'u_starPosition');
    this.simulationTimeLocation = requireUniform(gl, this.program, 'u_simulationTime');
    this.vegetationLocation = requireUniform(
      gl,
      this.program,
      'u_planetVegetation[0]',
    );
    this.previousPlanetPositionsLocation = requireUniform(
      gl,
      this.program,
      'u_previousPlanetPositions[0]',
    );
    this.currentPlanetPositionsLocation = requireUniform(
      gl,
      this.program,
      'u_currentPlanetPositions[0]',
    );
    this.planetRadiiLocation = requireUniform(
      gl,
      this.program,
      'u_planetRadii[0]',
    );
    this.collisionRadiiLocation = requireUniform(
      gl,
      this.program,
      'u_collisionRadii[0]',
    );
    this.bodyMaterialLocation = requireUniform(gl, this.program, 'u_bodyMaterial[0]');
    this.bodyClimateLocation = requireUniform(gl, this.program, 'u_bodyClimate[0]');
    this.bodyIceColourLocation = requireUniform(gl, this.program, 'u_bodyIceColour[0]');
    this.bodyCloudColourLocation = requireUniform(gl, this.program, 'u_bodyCloudColour[0]');
    this.bodyLowColorLocation = requireUniform(gl, this.program, 'u_bodyLowColor[0]');
    this.bodyHighColorLocation = requireUniform(gl, this.program, 'u_bodyHighColor[0]');
    this.bodyLiquidColorLocation = requireUniform(gl, this.program, 'u_bodyLiquidColor[0]');
    this.bodyAtmosphereColorLocation = requireUniform(
      gl,
      this.program,
      'u_bodyAtmosphereColor[0]',
    );
    } catch (error) {
      gl.bindVertexArray(null);
      for (const release of rollback.reverse()) release();
      throw error;
    }
  }

  public update(state: RendererState): void {
    this.renderState = state;
    this.count = state.count;
    this.upload(this.buffers[0], state.previousX);
    this.upload(this.buffers[1], state.previousY);
    this.upload(this.buffers[2], state.currentX);
    this.upload(this.buffers[3], state.currentY);
    this.upload(this.buffers[4], state.mass);
    this.upload(this.buffers[5], state.kind);
    this.vegetation.set(state.planetaryVegetation);
    for (let slot = 0; slot < CELESTIAL_BODY_SPECS.length; slot += 1) {
      const planet = CELESTIAL_BODY_SPECS[slot];
      if (planet === undefined) {
        continue;
      }
      const offset = slot * 2;
      const packed = slot * CLIMATE_RENDER_STRIDE;
      const materialOffset = slot * 4;
      const climate = state.planetaryClimate;
      this.bodyLiquidColor[materialOffset + 3] = climate[packed + C.plasma] ?? 0;
      this.bodyMaterial[materialOffset + 1] = climate[packed + C.atmosphere] ?? 0;
      this.bodyMaterial[materialOffset + 2] = climate[packed + C.sea] ?? 0;
      this.bodyClimate.set([
        climate[packed + C.clouds] ?? 0, climate[packed + C.ice] ?? 0,
        climate[packed + C.albedo] ?? 0.3, climate[packed + C.temperature] ?? 288,
      ], materialOffset);
      for (let component = 0; component < 3; component += 1) {
        const mineral = climate[packed + C.groundColour + component] ?? 0;
        this.bodyLowColor[materialOffset + component] = mineral * 0.48;
        this.bodyHighColor[materialOffset + component] = Math.min(1, mineral * 1.55);
        this.bodyAtmosphereColor[materialOffset + component] = climate[packed + C.atmosphereColour + component] ?? 0;
        this.bodyLiquidColor[materialOffset + component] = climate[packed + C.liquidColour + component] ?? 0;
        this.bodyIceColour[materialOffset + component] = climate[packed + C.iceColour + component] ?? 0;
        this.bodyCloudColour[materialOffset + component] = climate[packed + C.cloudColour + component] ?? 0;
      }
      this.previousPlanetPositions[offset] = state.previousX[planet.index] ?? 0;
      this.previousPlanetPositions[offset + 1] =
        state.previousY[planet.index] ?? 0;
      this.currentPlanetPositions[offset] = state.currentX[planet.index] ?? 0;
      this.currentPlanetPositions[offset + 1] =
        state.currentY[planet.index] ?? 0;
    }
  }

  public drawRange(
    interpolation: number,
    pointScale: number,
    worldPointScale: number,
    projectionScaleX: number,
    projectionScaleY: number,
    cameraCenterX: number,
    cameraCenterY: number,
    cameraZoom: number,
    first: number,
    count: number,
  ): void {
    if (count <= 0) {
      return;
    }
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.uniform1f(this.interpolationLocation, interpolation);
    gl.uniform1f(this.pointScaleLocation, pointScale);
    gl.uniform1f(this.worldPointScaleLocation, worldPointScale);
    gl.uniform2f(
      this.projectionScaleLocation,
      projectionScaleX,
      projectionScaleY,
    );
    gl.uniform2f(this.viewportSizeLocation, this.gl.canvas.width, this.gl.canvas.height);
    gl.uniform2f(this.cameraCenterLocation, cameraCenterX, cameraCenterY);
    gl.uniform1f(this.cameraZoomLocation, cameraZoom);
    const blend = Math.max(0, Math.min(1, interpolation));
    gl.uniform2f(
      this.starPositionLocation,
      (this.statePreviousX(0) * (1 - blend)) + this.stateCurrentX(0) * blend,
      (this.statePreviousY(0) * (1 - blend)) + this.stateCurrentY(0) * blend,
    );
    gl.uniform1f(
      this.simulationTimeLocation,
      (this.renderState?.previousSimulatedSeconds ?? 0) * (1 - blend) +
        (this.renderState?.currentSimulatedSeconds ?? 0) * blend,
    );
    gl.uniform1fv(this.vegetationLocation, this.vegetation);
    gl.uniform2fv(
      this.previousPlanetPositionsLocation,
      this.previousPlanetPositions,
    );
    gl.uniform2fv(
      this.currentPlanetPositionsLocation,
      this.currentPlanetPositions,
    );
    gl.uniform1fv(this.planetRadiiLocation, this.planetRadii);
    gl.uniform1fv(this.collisionRadiiLocation, this.collisionRadii);
    gl.uniform4fv(this.bodyMaterialLocation, this.bodyMaterial);
    gl.uniform4fv(this.bodyClimateLocation, this.bodyClimate);
    gl.uniform4fv(this.bodyIceColourLocation, this.bodyIceColour);
    gl.uniform4fv(this.bodyCloudColourLocation, this.bodyCloudColour);
    gl.uniform4fv(this.bodyLowColorLocation, this.bodyLowColor);
    gl.uniform4fv(this.bodyHighColorLocation, this.bodyHighColor);
    gl.uniform4fv(this.bodyLiquidColorLocation, this.bodyLiquidColor);
    gl.uniform4fv(
      this.bodyAtmosphereColorLocation,
      this.bodyAtmosphereColor,
    );
    gl.bindVertexArray(this.vertexArray);
    const byteOffset = first * Float32Array.BYTES_PER_ELEMENT;
    for (let index = 0; index < this.buffers.length; index += 1) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers[index] ?? null);
      gl.vertexAttribPointer(
        this.attributeLocations[index] ?? 0,
        1,
        gl.FLOAT,
        false,
        0,
        byteOffset,
      );
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.entityIndexBuffer);
    gl.vertexAttribPointer(
      this.entityIndexLocation,
      1,
      gl.FLOAT,
      false,
      0,
      byteOffset,
    );
    gl.drawArraysInstanced(
      gl.TRIANGLE_STRIP,
      0,
      4,
      Math.min(count, this.count - first),
    );
    gl.bindVertexArray(null);
  }

  public dispose(): void {
    for (const buffer of this.buffers) {
      this.gl.deleteBuffer(buffer);
    }
    this.gl.deleteBuffer(this.cornerBuffer);
    this.gl.deleteBuffer(this.entityIndexBuffer);
    this.gl.deleteVertexArray(this.vertexArray);
    this.gl.deleteProgram(this.program);
  }

  private upload(
    buffer: WebGLBuffer | undefined,
    values: Float32Array,
  ): void {
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer ?? null);
    this.gl.bufferSubData(
      this.gl.ARRAY_BUFFER,
      0,
      values,
      0,
      this.count,
    );
  }

  private statePreviousX(index: number): number {
    return this.renderState?.previousX[index] ?? 0;
  }

  private statePreviousY(index: number): number {
    return this.renderState?.previousY[index] ?? 0;
  }

  private stateCurrentX(index: number): number {
    return this.renderState?.currentX[index] ?? 0;
  }

  private stateCurrentY(index: number): number {
    return this.renderState?.currentY[index] ?? 0;
  }
}

function appearanceColorArray(
  key: 'lowColor' | 'highColor' | 'liquidColor' | 'atmosphereColor',
): Float32Array {
  return Float32Array.from(
    CELESTIAL_BODY_SPECS.flatMap(({ appearance }) => [
      appearance[key][0],
      appearance[key][1],
      appearance[key][2],
      appearance.terrainSeed,
    ]),
  );
}
